import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import CustomTooltip from '@components/ringil3/Tooltip/CustomTooltip.tsx';
import { balancedLabelColumns } from './labelGridLayout';

interface Label {
  labelValue: string;
  labelColor?: string;
}

interface QuoteLabelsProps {
  labels: Label[];
  isExpanded: boolean;
  onToggle: () => void;
}

// Minimum label width and column gap, in rem — collapsed shows thin colour bars, expanded shows the (truncated) label text.
const COLLAPSED = { minCell: 2, gap: 0 };
const EXPANDED = { minCell: 5, gap: 0.1 };

const remToPx = (rem: number) => rem * parseFloat(getComputedStyle(document.documentElement).fontSize);

// Grid of equal-width cells: as many labels per row as fit at the minimum width, with the rows balanced (7 that fit 6 per row
// become 4 + 3) so a wrapped label never ends up alone and stretched across the whole strip. The column count needs the strip's
// width, so it is measured; until then auto-fit gives the same layout without the balancing.
const ExpandableLabelWrapper: React.FC<QuoteLabelsProps> = ({ labels, isExpanded, onToggle }) => {
  const idPrefix = useId();
  const labelId = (index: number) => `${idPrefix}-label-${index}`;
  const containerRef = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState<number>();
  const count = labels?.length ?? 0;
  const sizing = isExpanded ? EXPANDED : COLLAPSED;

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const update = () => setColumns(balancedLabelColumns(count, node.clientWidth, remToPx(sizing.minCell), remToPx(sizing.gap)));
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [count, sizing]);

  return (
    <div
      ref={containerRef}
      className={`grid w-full font-dm-sans my-2
      ${
        isExpanded
          ? 'grid-cols-[repeat(auto-fit,minmax(5rem,1fr))] gap-[0.1rem] rounded-none overflow-visible'
          : 'grid-cols-[repeat(auto-fit,minmax(2rem,1fr))] gap-x-0 gap-y-[0.1rem] rounded-md overflow-hidden'
      }
    `}
      style={columns ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` } : undefined}
    >
      {(labels ?? [])
        .slice()
        .sort((a, b) => a.labelValue.localeCompare(b.labelValue))
        .map((t, tIndex) => (
          <React.Fragment key={tIndex}>
            <div
              id={labelId(tIndex)}
              className={`flex flex-row min-w-0 py-[0.3rem] text-white cursor-pointer font-dm-sans justify-center
              ${isExpanded ? 'px-2 rounded-md' : 'rounded-none'}
            `}
              style={{ backgroundColor: t.labelColor || 'black', margin: 0 }}
              title={isExpanded ? undefined : t.labelValue}
              onClick={onToggle}
            >
              {isExpanded && <div className="truncate">{t.labelValue}</div>}
            </div>
            {/* Expanded labels may be truncated to their cell — show the full name on hover. */}
            {isExpanded && <CustomTooltip targetSelector={`#${CSS.escape(labelId(tIndex))}`} content={t.labelValue} position="top" />}
          </React.Fragment>
        ))}
    </div>
  );
};

export default ExpandableLabelWrapper;
